import React, { useEffect, useState } from 'react';
import { Alert, Button, Spin, Typography, Descriptions } from 'antd';
import ResponsiveModal from './ResponsiveModal';
import { getEsimQrCode, getEsimCodes, type EsimCodesResult } from '../services/adminService';

const { Text, Paragraph } = Typography;

interface EsimQrModalProps {
  iccid: string | null;
  open: boolean;
  onClose: () => void;
}

// KAN-94/KAN-95: show an eSIM's activation QR (and codes) so a reseller can hand
// it to the end user. Both are re-fetched live from VRS (§6.5.2/6.5.3) — nothing
// sensitive is stored. reseller_admin+ is enforced by the backend.
const EsimQrModal: React.FC<EsimQrModalProps> = ({ iccid, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [codes, setCodes] = useState<EsimCodesResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !iccid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setQrDataUrl(null);
    setCodes(null);
    // QR is the important bit; codes are best-effort (manual-entry fallback).
    Promise.allSettled([getEsimQrCode(iccid), getEsimCodes(iccid)])
      .then(([qr, cd]) => {
        if (cancelled) return;
        if (qr.status === 'fulfilled') setQrDataUrl(qr.value.dataUrl);
        else setError('Could not fetch the QR code from the provider.');
        if (cd.status === 'fulfilled') setCodes(cd.value);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, iccid]);

  const activationCode =
    codes && typeof codes.activationCode === 'string' ? codes.activationCode : null;
  const confirmationCode =
    codes && typeof codes.confirmationCode === 'string' ? codes.confirmationCode : null;

  return (
    <ResponsiveModal
      title={`eSIM activation — ${iccid || ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <>
          {error && <Alert style={{ marginBottom: 16 }} type="error" showIcon message={error} />}

          <Paragraph type="secondary">
            Scan this QR with the end user's phone (Settings → Mobile Data → Add eSIM), or enter the
            activation details manually.
          </Paragraph>

          {qrDataUrl ? (
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <img
                src={qrDataUrl}
                alt={`eSIM activation QR for ${iccid}`}
                style={{ maxWidth: 260, width: '100%', border: '1px solid #eee', padding: 8 }}
              />
              <div style={{ marginTop: 8 }}>
                <a href={qrDataUrl} download={`esim-${iccid}.png`}>
                  <Button size="small">Download QR</Button>
                </a>
              </div>
            </div>
          ) : (
            !error && <Text type="secondary">QR not available.</Text>
          )}

          {(activationCode || confirmationCode) && (
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              {activationCode && (
                <Descriptions.Item label="Activation code">
                  <Text copyable>{activationCode}</Text>
                </Descriptions.Item>
              )}
              {confirmationCode && (
                <Descriptions.Item label="Confirmation code">
                  <Text copyable>{confirmationCode}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          )}

          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <Button type="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </>
      )}
    </ResponsiveModal>
  );
};

export default EsimQrModal;
